// ============================================================
// Analysis Context - "Golden Thread" State Management
// ============================================================
// Provides unified AIAG-VDA analysis data to all components
// Maintains data flow: Structure → Function → Failure → Risk
// ============================================================

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAppContext } from './AppContext';
import { useAuth } from './AuthContext';
import {
    ProcessStep,
    ProcessFunction,
    ProductRequirement,
    PfmeaItem,
    CauseCategory,
    WorkInstruction,

    WorkElement,
} from '../types';
import {
    FailureChain,
    ProjectMember,
    extractProcessStepsFromFlow,
    saveProcessSteps,
    saveProductRequirements,
    saveProcessFunctions,
    createProcessFunction,
    saveFailureChains,
    createFailureChain,
    syncFailureChainsToPfmea,
    getProjectMembers,
    addProjectMember,
    removeProjectMember,
    loadAnalysisSnapshot,
    getWorkInstructions,

    saveWorkInstructions,
    saveWorkElements,
} from '../services/analysisService';

// ============================================================
// CONTEXT TYPES
// ============================================================

interface AnalysisContextType {
    // Analysis Data (Golden Thread)
    processSteps: ProcessStep[];
    productRequirements: ProductRequirement[];
    processFunctions: ProcessFunction[];
    failureChains: FailureChain[];
    workInstructions: WorkInstruction[];

    workElements: WorkElement[];

    // Loading / Sync Status
    isLoading: boolean;
    isSyncing: boolean;
    lastSyncedAt: string | null;

    // Structure Analysis Actions (Step 2)
    extractFromFlow: (nodes: any[]) => void;
    addProcessStep: (step: ProcessStep) => void;
    updateProcessStep: (id: string, updates: Partial<ProcessStep>) => void;

    // Function Analysis Actions (Step 3)
    linkFunction: (stepId: string, reqId: string, description: string) => void;
    unlinkFunction: (functionId: string) => void;
    updateFunction: (id: string, updates: Partial<ProcessFunction>) => void;

    // Requirement Actions (Step 3 Addition)
    addRequirement: (req: ProductRequirement) => void;
    updateRequirement: (id: string, updates: Partial<ProductRequirement>) => void;
    setProductRequirements: (reqs: ProductRequirement[]) => void;

    // Failure Analysis Actions (Step 4)
    addFailureChain: (stepId: string, functionId: string, causeCategory: CauseCategory) => FailureChain;
    updateFailureChain: (id: string, updates: Partial<FailureChain>) => void;
    deleteFailureChain: (id: string) => void;

    // Results Documentation (Step 7)
    addWorkInstruction: (instruction: WorkInstruction) => void;
    updateWorkInstruction: (id: string, updates: Partial<WorkInstruction>) => void;
    deleteWorkInstruction: (id: string) => void;

    // Sync Actions (Step 5)
    syncToPfmea: () => { newItems: Partial<PfmeaItem>[]; count: number };

    // Project Access (RBAC)
    projectMembers: ProjectMember[];
    addMember: (email: string, role: ProjectMember['role']) => Promise<void>;
    removeMember: (uid: string) => Promise<void>;
    refreshMembers: () => Promise<void>;

    // Refresh Data
    refreshAnalysis: () => Promise<void>;
    saveAnalysis: () => Promise<void>;
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(undefined);

// ============================================================
// PROVIDER COMPONENT
// ============================================================

export const AnalysisProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const {
        activeProjectName,
        pfmeaItems,
        setPfmeaItems,
        setProcessFunctions: setAppFunctions,
        processFunctions: appFunctions,
        productRequirements: _appRequirements,
        setProductRequirements
    } = useAppContext();
    const { user: _user } = useAuth();

    // Analysis State
    const [processSteps, setProcessSteps] = useState<ProcessStep[]>([]);
    const [productRequirements, setProductRequirementsState] = useState<ProductRequirement[]>([]);
    const [processFunctions, setProcessFunctions] = useState<ProcessFunction[]>([]);
    const [failureChains, setFailureChains] = useState<FailureChain[]>([]);
    const [workInstructions, setWorkInstructions] = useState<WorkInstruction[]>([]);
    const [workElements, setWorkElements] = useState<WorkElement[]>([]);
    const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);

    // Status
    const [isLoading, setIsLoading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

    // ============================================================
    // DATA LOADING
    // ============================================================

    const loadAnalysisData = useCallback(async () => {
        if (!activeProjectName) return;

        setIsLoading(true);
        try {
            const [snapshot, instructions] = await Promise.all([
                loadAnalysisSnapshot(activeProjectName),
                getWorkInstructions(activeProjectName)
            ]);

            setProcessSteps(snapshot.processSteps);
            setProductRequirementsState(snapshot.productRequirements);
            setProcessFunctions(snapshot.processFunctions);
            setProcessFunctions(snapshot.processFunctions);
            setFailureChains(snapshot.failureChains);
            setWorkElements(snapshot.workElements || []);
            setWorkInstructions(instructions);
            setLastSyncedAt(snapshot.lastSyncedAt);

            // Sync to AppContext for backwards compatibility
            setAppFunctions(snapshot.processFunctions);
            setProductRequirements(snapshot.productRequirements);
        } catch (error) {
            console.error('[AnalysisContext] Load failed:', error);
        } finally {
            setIsLoading(false);
        }
    }, [activeProjectName, setAppFunctions, setProductRequirements]);

    const loadMembers = useCallback(async () => {
        if (!activeProjectName) return;
        try {
            const members = await getProjectMembers(activeProjectName);
            setProjectMembers(members);
        } catch (error) {
            console.error('[AnalysisContext] Load members failed:', error);
        }
    }, [activeProjectName]);

    // Load data when project changes
    useEffect(() => {
        if (activeProjectName) {
            loadAnalysisData();
            loadMembers();
        }
    }, [activeProjectName, loadAnalysisData, loadMembers]);

    // ============================================================
    // STRUCTURE ANALYSIS ACTIONS (Step 2)
    // ============================================================

    const extractFromFlow = useCallback((nodes: any[]) => {
        if (!activeProjectName) return;
        const steps = extractProcessStepsFromFlow(nodes, activeProjectName);
        setProcessSteps(steps);
    }, [activeProjectName]);

    const addProcessStep = useCallback((step: ProcessStep) => {
        setProcessSteps(prev => [...prev, step]);
    }, []);

    const updateProcessStep = useCallback((id: string, updates: Partial<ProcessStep>) => {
        setProcessSteps(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
    }, []);

    // ============================================================
    // FUNCTION ANALYSIS ACTIONS (Step 3)
    // ============================================================

    const linkFunction = useCallback((stepId: string, reqId: string, description: string) => {
        const newFunc = createProcessFunction(stepId, reqId, description);
        setProcessFunctions(prev => [...prev, newFunc]);
        setAppFunctions([...appFunctions, newFunc]);
    }, [setAppFunctions, appFunctions]);

    const unlinkFunction = useCallback((functionId: string) => {
        setProcessFunctions(prev => prev.filter(f => f.id !== functionId));
        setAppFunctions(appFunctions.filter(f => f.id !== functionId));
    }, [setAppFunctions, appFunctions]);

    const updateFunction = useCallback((id: string, updates: Partial<ProcessFunction>) => {
        const update = (prev: ProcessFunction[]) =>
            prev.map(f => f.id === id ? { ...f, ...updates } : f);
        setProcessFunctions(update);
        setAppFunctions(update(appFunctions));
    }, [setAppFunctions, appFunctions]);

    // ============================================================
    // REQUIREMENT ACTIONS (Step 3 Addition)
    // ============================================================

    const addRequirement = useCallback((req: ProductRequirement) => {
        setProductRequirementsState(prev => [...prev, req]);
        setProductRequirements([...productRequirements, req]);
    }, [productRequirements, setProductRequirements]);

    const updateRequirement = useCallback((id: string, updates: Partial<ProductRequirement>) => {
        const update = (prev: ProductRequirement[]) =>
            prev.map(r => r.id === id ? { ...r, ...updates } : r);
        setProductRequirementsState(update);
        const updated = update(productRequirements);
        setProductRequirements(updated);
    }, [productRequirements, setProductRequirements]);

    const setProductRequirementsFull = useCallback((reqs: ProductRequirement[]) => {
        setProductRequirementsState(reqs);
        setProductRequirements(reqs);
    }, [setProductRequirements]);

    // ============================================================
    // FAILURE ANALYSIS ACTIONS (Step 4)
    // ============================================================

    const addFailureChain = useCallback((
        stepId: string,
        functionId: string,
        causeCategory: CauseCategory
    ): FailureChain => {
        if (!activeProjectName) {
            throw new Error('No active project');
        }
        const chain = createFailureChain(activeProjectName, stepId, functionId, causeCategory);
        setFailureChains(prev => [...prev, chain]);
        return chain;
    }, [activeProjectName]);

    const updateFailureChain = useCallback((id: string, updates: Partial<FailureChain>) => {
        setFailureChains(prev => prev.map(c =>
            c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c
        ));
    }, []);

    const deleteFailureChain = useCallback((id: string) => {
        setFailureChains(prev => prev.filter(c => c.id !== id));
    }, []);

    // ============================================================
    // SYNC ACTIONS (Step 5)
    // ============================================================

    const syncToPfmea = useCallback(() => {
        const { newItems, updatedChains } = syncFailureChainsToPfmea(
            failureChains,
            processSteps,
            processFunctions,
            pfmeaItems
        );

        // Update PFMEA Items in AppContext
        setPfmeaItems([...pfmeaItems, ...newItems as any]);

        // Update Failure Chains with linked IDs
        setFailureChains(updatedChains);

        return { newItems, count: newItems.length };
    }, [failureChains, processSteps, processFunctions, pfmeaItems, setPfmeaItems]);

    // ============================================================
    // RBAC ACTIONS
    // ============================================================

    const addMember = useCallback(async (email: string, role: ProjectMember['role']) => {
        if (!activeProjectName) return;
        await addProjectMember(activeProjectName, email, role);
        await loadMembers();
    }, [activeProjectName, loadMembers]);

    const removeMember = useCallback(async (uid: string) => {
        if (!activeProjectName) return;
        await removeProjectMember(activeProjectName, uid);
        await loadMembers();
    }, [activeProjectName, loadMembers]);

    const refreshMembers = useCallback(async () => {
        await loadMembers();
    }, [loadMembers]);

    // ============================================================
    // SAVE / REFRESH
    // ============================================================

    const saveAnalysis = useCallback(async () => {
        if (!activeProjectName) return;

        setIsSyncing(true);
        try {
            await Promise.all([
                saveProcessSteps(activeProjectName, processSteps),
                saveProductRequirements(activeProjectName, productRequirements),
                saveProcessFunctions(activeProjectName, processFunctions),
                saveFailureChains(activeProjectName, failureChains),
                saveFailureChains(activeProjectName, failureChains),
                saveWorkInstructions(activeProjectName, workInstructions),
                saveWorkElements(activeProjectName, workElements),
            ]);
            setLastSyncedAt(new Date().toISOString());
        } catch (error) {
            console.error('[AnalysisContext] Save failed:', error);
        } finally {
            setIsSyncing(false);
        }
    }, [activeProjectName, processSteps, productRequirements, processFunctions, failureChains, workInstructions]);

    const refreshAnalysis = useCallback(async () => {
        await loadAnalysisData();
    }, [loadAnalysisData]);

    // ============================================================
    // CONTEXT VALUE
    // ============================================================

    const value: AnalysisContextType = {
        // Data
        processSteps,
        productRequirements,
        processFunctions,
        failureChains,
        workInstructions,
        workElements,

        // Status
        isLoading,
        isSyncing,
        lastSyncedAt,

        // Structure Analysis
        extractFromFlow,
        addProcessStep,
        updateProcessStep,

        // Function Analysis
        linkFunction,
        unlinkFunction,
        updateFunction,

        // Requirements
        addRequirement,
        updateRequirement,
        setProductRequirements: setProductRequirementsFull,

        // Work Instructions
        addWorkInstruction: (instr: WorkInstruction) => setWorkInstructions(prev => [...prev, instr]),
        updateWorkInstruction: (id: string, updates: Partial<WorkInstruction>) =>
            setWorkInstructions(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i)),
        deleteWorkInstruction: (id: string) =>
            setWorkInstructions(prev => prev.filter(i => i.id !== id)),

        // Failure Analysis
        addFailureChain,
        updateFailureChain,
        deleteFailureChain,

        // Sync
        syncToPfmea,

        // RBAC
        projectMembers,
        addMember,
        removeMember,
        refreshMembers,

        // Refresh/Save
        refreshAnalysis,
        saveAnalysis,
    };

    return (
        <AnalysisContext.Provider value={value}>
            {children}
        </AnalysisContext.Provider>
    );
};

// ============================================================
// HOOK
// ============================================================

export const useAnalysis = () => {
    const context = useContext(AnalysisContext);
    if (!context) {
        throw new Error('useAnalysis must be used within an AnalysisProvider');
    }
    return context;
};

export default AnalysisContext;
